/**
 * InventoryCapturePanel — Phase 4e.B (Feb 2026)
 *
 * Inventur-Erfassung mit Pflicht-Lagerort + 1-6 Foto-Belegen pro Artikel.
 * Workflow:
 *   1) Lagerort wählen (Dropdown oder QR-Code-Payload eingeben)
 *   2) Artikel wählen / SKU eingeben
 *   3) Gezählte Menge eintragen
 *   4) 1-6 Fotos hochladen (Capture via input[capture=environment] auf Mobile)
 *   5) "Buchen" → speichert StockMovement + aktualisiert Artikel
 *
 * Live-Feed: Die letzten 10 Buchungen werden rechts angezeigt mit Thumbnails.
 */
import { useEffect, useState } from "react";
import { Loader2, Camera, X, Check, MapPin, Hash, Plus, Boxes, Sparkles, ScanLine } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import PhotoThumb from "./PhotoThumb";
import AiEnrichArticleModal from "./AiEnrichArticleModal";
import EanCameraScanner from "../wms/EanCameraScanner";
import { QRCodeSVG } from "qrcode.react";

const MAX_PHOTOS = 6;

export default function InventoryCapturePanel() {
    const [locations, setLocations] = useState([]);
    const [locationId, setLocationId] = useState("");
    const [scanInput, setScanInput] = useState("");
    const [sku, setSku] = useState("");
    const [article, setArticle] = useState(null);
    const [searching, setSearching] = useState(false);
    const [countedQty, setCountedQty] = useState("");
    const [photos, setPhotos] = useState([]); // [{path, content_type}]
    const [uploading, setUploading] = useState(false);
    const [booking, setBooking] = useState(false);
    const [feed, setFeed] = useState([]); // last 10 booked items
    const [showAiModal, setShowAiModal] = useState(false);
    const [scanMode, setScanMode] = useState(null); // "location" | "article" | null
    const [mobileSess, setMobileSess] = useState(null);
    const [creatingSess, setCreatingSess] = useState(false);

    const startMobileSession = async () => {
        setCreatingSess(true);
        try {
            const { data } = await api.post("/articles/inventory-scan-sessions");
            setMobileSess(data);
        } catch (e) { toast.error(e.response?.data?.detail || "QR-Session fehlgeschlagen"); }
        setCreatingSess(false);
    };

    const printInvQr = () => {
        const svg = document.querySelector('[data-testid="inv-mobile-qr"] svg');
        if (!svg || !mobileSess) { toast.error("Kein QR-Code vorhanden"); return; }
        const w = window.open("", "_blank", "width=480,height=680");
        if (!w) { toast.error("Popup blockiert — bitte Pop-ups erlauben"); return; }
        w.document.write(`<html><head><title>Inventur Handy-Scan QR</title>
        <style>body{font-family:system-ui,Arial,sans-serif;text-align:center;padding:28px;color:#111827}
        .box{border:3px solid #111827;display:inline-block;padding:16px;border-radius:8px;margin:12px 0}
        h1{font-size:18px;margin:0 0 4px} h2{font-size:13px;color:#374151;margin:0 0 16px}
        p{font-size:11px;color:#6b7280;word-break:break-all;max-width:360px;margin:8px auto}</style></head>
        <body><h1>Inventur erfassen</h1><h2>Am Handy scannen</h2>
        <div class="box">${svg.outerHTML}</div><p>${mobileSess.portal_url}</p>
        <p>QR mit der Handy-Kamera scannen, um die mobile Inventur zu öffnen (kein Login nötig).</p>
        <script>window.onload=function(){setTimeout(function(){window.print();},300);};</script>
        </body></html>`);
        w.document.close();
    };

    useEffect(() => {
        api.get("/dealer/storage-locations").then((r) => setLocations(r.data || [])).catch(() => {});
    }, []);

    // Resolve scanned payload `tw-loc:{dealer_id}:{location_id}` → set locationId
    useEffect(() => {
        if (!scanInput) return;
        const match = scanInput.match(/^tw-loc:[^:]+:([0-9a-f-]+)$/i);
        if (match) {
            const lid = match[1];
            const loc = locations.find((l) => l.id === lid);
            if (loc) {
                setLocationId(lid);
                setScanInput("");
                toast.success(`Lagerort gesetzt: ${loc.code}`);
            } else {
                toast.error("Lagerort nicht gefunden — gehört dieser QR zu Ihrem Konto?");
            }
        }
    }, [scanInput, locations]);

    const findArticle = async () => {
        const q = sku.trim();
        if (!q) { toast.error("Bitte SKU eingeben"); return; }
        setSearching(true);
        try {
            const r = await api.get("/articles", { params: { search: q, limit: 5 } });
            const match = (r.data || []).find((a) => (a.sku || "").toLowerCase() === q.toLowerCase())
                || (r.data || [])[0];
            if (!match) { toast.error("Artikel nicht gefunden"); setArticle(null); return; }
            setArticle(match);
            setCountedQty(String(match.stock_qty ?? ""));
            setPhotos((match.photo_paths || []).map((p) => ({ path: p })).slice(0, MAX_PHOTOS));
            toast.success(`${match.name} geladen (Bestand ${match.stock_qty})`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Suche fehlgeschlagen");
        }
        setSearching(false);
    };

    // Jul-2026 — Live-Kamera-Scan (Lagerort-QR & Artikel-EAN) via @zxing.
    const resolveLocationScan = (payload) => {
        setScanMode(null);
        const p = (payload || "").trim();
        const m = p.match(/^tw-loc:[^:]+:([0-9a-f-]+)$/i);
        const loc = m ? locations.find((l) => l.id === m[1])
                      : locations.find((l) => (l.code || "").toLowerCase() === p.toLowerCase());
        if (loc) { setLocationId(loc.id); setScanInput(""); toast.success(`Lagerort gesetzt: ${loc.code}`); }
        else { toast.error("Lagerort nicht gefunden — QR/Code zu Ihrem Konto?"); }
    };

    const findArticleByCode = async (code) => {
        setScanMode(null);
        const c = (code || "").trim();
        if (!c) return;
        setSearching(true);
        try {
            let match = null;
            try {
                const r = await api.get(`/articles/by-ean/${encodeURIComponent(c)}`);
                match = r.data || null;
            } catch { /* fallback auf Suche */ }
            if (!match) {
                const r = await api.get("/articles", { params: { search: c, limit: 5 } });
                match = (r.data || []).find((a) => (a.sku || "").toLowerCase() === c.toLowerCase()) || (r.data || [])[0];
            }
            if (!match) { toast.error("Artikel nicht gefunden"); setArticle(null); return; }
            setSku(match.sku || c);
            setArticle(match);
            setCountedQty(String(match.stock_qty ?? ""));
            setPhotos((match.photo_paths || []).map((p) => ({ path: p })).slice(0, MAX_PHOTOS));
            toast.success(`${match.name} geladen (Bestand ${match.stock_qty})`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Suche fehlgeschlagen");
        }
        setSearching(false);
    };

    const onPhotoChange = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (photos.length >= MAX_PHOTOS) {
            toast.error(`Maximal ${MAX_PHOTOS} Fotos pro Artikel`);
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/wms/photos/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPhotos((p) => [...p, { path: r.data.path, content_type: r.data.content_type }]);
            toast.success("Foto hochgeladen");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload fehlgeschlagen");
        }
        setUploading(false);
    };

    const removePhoto = (path) => {
        setPhotos((p) => p.filter((x) => x.path !== path));
    };

    const reset = () => {
        setArticle(null);
        setSku("");
        setCountedQty("");
        setPhotos([]);
    };

    const book = async () => {
        if (!locationId) { toast.error("Bitte einen Lagerort wählen"); return; }
        if (!article) { toast.error("Bitte einen Artikel laden"); return; }
        const qty = Number(countedQty);
        if (!Number.isFinite(qty) || qty < 0) { toast.error("Bitte gültige Menge eingeben"); return; }
        if (photos.length === 0) {
            if (!window.confirm("Keine Fotos angehängt. Trotzdem buchen?")) return;
        }
        setBooking(true);
        try {
            const body = {
                reference: `Inventur ${new Date().toLocaleDateString("de-DE")}`,
                method: "Inventur-Erfassung",
                items: [{
                    article_id: article.id,
                    counted_qty: qty,
                    location_id: locationId,
                    photo_paths: photos.map((p) => p.path),
                }],
            };
            const r = await api.post("/articles/inventory-session", body);
            const it = r.data.items?.[0];
            if (it?.skipped) {
                toast.error(`Buchung abgelehnt: ${it.skipped}`);
            } else {
                toast.success(`Gebucht: ${article.name} → ${qty} ${article.unit || "Stück"}`);
                // Live-Feed (letzte 10)
                const loc = locations.find((l) => l.id === locationId);
                setFeed((f) => [{
                    id: r.data.session_id,
                    article: article.name,
                    sku: article.sku,
                    qty,
                    location_code: loc?.code || "—",
                    delta: it?.delta || 0,
                    photo_paths: it?.photo_paths || [],
                    at: new Date(),
                }, ...f].slice(0, 10));
                reset();
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Buchung fehlgeschlagen");
        }
        setBooking(false);
    };

    const selectedLoc = locations.find((l) => l.id === locationId);
    const token = sessionStorage.getItem("tw_token") || "";

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4" data-testid="inventory-capture-panel">
            {/* MAIN FORM */}
            <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-xs text-blue-900">
                    <strong>Workflow:</strong> 1. Lagerort scannen (QR) oder wählen → 2. Artikel-SKU eingeben / scannen → 3. Menge + Fotos → <strong>Buchen</strong>.
                </div>

                {/* Jul-2026 — Mobile Inventur per QR aufs Handy holen (login-frei). */}
                <div className="bg-white border border-gray-200 rounded-sm p-3">
                    {!mobileSess ? (
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs text-gray-600">Lieber am Handy erfassen?</span>
                            <Button type="button" size="sm" variant="outline" onClick={startMobileSession} disabled={creatingSess} className="border-2 h-8 text-xs ml-auto" data-testid="inv-mobile-start">
                                {creatingSess ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ScanLine className="w-4 h-4 mr-1" /> Mobil scannen (QR)</>}
                            </Button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-3 flex-wrap">
                            <div className="bg-white p-2 border-2 border-[#111827] rounded-sm" data-testid="inv-mobile-qr">
                                <QRCodeSVG value={mobileSess.portal_url} size={110} level="M" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-xs font-bold text-[#111827]">Mit dem Handy scannen</div>
                                <div className="text-[10px] text-gray-500 break-all">{mobileSess.portal_url}</div>
                                <div className="flex gap-2 mt-1.5">
                                    <Button type="button" size="sm" variant="outline" onClick={printInvQr} className="border-2 h-7 text-xs" data-testid="inv-mobile-print-qr"><Camera className="w-3.5 h-3.5 mr-1" /> QR drucken</Button>
                                    <Button type="button" size="sm" variant="ghost" onClick={() => setMobileSess(null)} className="h-7 text-xs text-gray-500">schließen</Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Lagerort */}
                <div className="bg-white border border-gray-200 rounded-sm p-4 space-y-2">
                    <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#FFCC00]" />
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Lagerort (Pflicht)</Label>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <Input
                            value={scanInput}
                            onChange={(e) => setScanInput(e.target.value)}
                            placeholder="QR scannen: tw-loc:…  (oder unten wählen)"
                            className="flex-1 rounded-sm h-9 font-mono-num text-xs"
                            data-testid="inventory-scan-location-input"
                        />
                        <Button type="button" variant="outline" onClick={() => setScanMode("location")} className="h-9 rounded-sm border-2 border-emerald-400 text-emerald-700 hover:bg-emerald-50 px-3" title="Lagerort-QR mit Kamera scannen" data-testid="inventory-scan-location-camera">
                            <ScanLine className="w-4 h-4" />
                        </Button>
                        <select
                            value={locationId}
                            onChange={(e) => setLocationId(e.target.value)}
                            className="rounded-sm border border-gray-300 h-9 px-2 text-sm flex-1 min-w-[180px]"
                            data-testid="inventory-location-select"
                        >
                            <option value="">— Lagerort wählen —</option>
                            {locations.map((l) => (
                                <option key={l.id} value={l.id}>{l.code} {l.name ? `· ${l.name}` : ""}</option>
                            ))}
                        </select>
                    </div>
                    {selectedLoc && (
                        <div className="text-xs text-emerald-700 flex items-center gap-1.5 mt-1" data-testid="inventory-location-selected">
                            <Check className="w-3.5 h-3.5" />
                            Aktiv: <strong>{selectedLoc.code}</strong> ({selectedLoc.name || [selectedLoc.hall, selectedLoc.rack, selectedLoc.shelf].filter(Boolean).join(" · ") || "—"})
                        </div>
                    )}
                </div>

                {/* Artikel */}
                <div className="bg-white border border-gray-200 rounded-sm p-4 space-y-2">
                    <div className="flex items-center gap-2">
                        <Hash className="w-4 h-4 text-[#FFCC00]" />
                        <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Artikel</Label>
                    </div>
                    <div className="flex gap-2">
                        <Input
                            value={sku}
                            onChange={(e) => setSku(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") findArticle(); }}
                            placeholder="SKU eingeben oder scannen"
                            className="flex-1 rounded-sm h-9 font-mono-num"
                            data-testid="inventory-sku-input"
                        />
                        <Button type="button" variant="outline" onClick={() => setScanMode("article")} className="h-9 rounded-sm border-2 border-emerald-400 text-emerald-700 hover:bg-emerald-50 px-3" title="Artikel-Barcode mit Kamera scannen" data-testid="inventory-scan-article-camera">
                            <ScanLine className="w-4 h-4" />
                        </Button>
                        <Button type="button" onClick={findArticle} disabled={searching} className="h-9 rounded-sm bg-[#111827] hover:bg-black text-white" data-testid="inventory-sku-load-btn">
                            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Laden"}
                        </Button>
                    </div>
                    {article && (
                        <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 mt-2" data-testid="inventory-article-card">
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                    <div className="font-bold text-sm">{article.name}</div>
                                    <div className="text-xs text-gray-600 mt-0.5 font-mono-num">SKU: {article.sku} · Bestand: {article.stock_qty} {article.unit || "Stück"}</div>
                                </div>
                                <Button
                                    type="button"
                                    onClick={() => setShowAiModal(true)}
                                    className="rounded-sm bg-gradient-to-r from-[#111827] to-[#1F2937] hover:opacity-90 text-white h-8 text-xs shrink-0"
                                    data-testid="inventory-ai-enrich-btn"
                                >
                                    <Sparkles className="w-3.5 h-3.5 mr-1 text-[#FFCC00]" />
                                    KI nachbearbeiten
                                </Button>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                                <div>
                                    <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Gezählte Menge *</Label>
                                    <Input
                                        type="number"
                                        step="any"
                                        value={countedQty}
                                        onChange={(e) => setCountedQty(e.target.value)}
                                        placeholder="0"
                                        className="rounded-sm mt-1 font-mono-num"
                                        data-testid="inventory-counted-qty-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Differenz</Label>
                                    <div className="rounded-sm mt-1 h-9 px-3 flex items-center bg-white border border-gray-200 font-mono-num font-bold" data-testid="inventory-delta-display">
                                        {(() => {
                                            const d = Number(countedQty) - Number(article.stock_qty || 0);
                                            if (!Number.isFinite(d)) return "—";
                                            return (d > 0 ? "+" : "") + d.toFixed(2);
                                        })()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Fotos */}
                {article && (
                    <div className="bg-white border border-gray-200 rounded-sm p-4 space-y-2">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Camera className="w-4 h-4 text-[#FFCC00]" />
                                <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Fotos ({photos.length}/{MAX_PHOTOS})</Label>
                            </div>
                            <label className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-sm border cursor-pointer ${photos.length >= MAX_PHOTOS ? "opacity-50 cursor-not-allowed bg-gray-50" : "bg-[#FFCC00] hover:bg-[#FFD633] border-[#FFCC00]"}`} data-testid="inventory-photo-add-label">
                                <Plus className="w-3.5 h-3.5" />
                                Foto hinzufügen
                                <input
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    className="hidden"
                                    disabled={photos.length >= MAX_PHOTOS || uploading}
                                    onChange={onPhotoChange}
                                    data-testid="inventory-photo-input"
                                />
                            </label>
                        </div>
                        {uploading && <div className="text-xs text-gray-500"><Loader2 className="w-3 h-3 inline animate-spin mr-1" />Lädt hoch…</div>}
                        {photos.length === 0 && !uploading && (
                            <div className="text-xs text-gray-400 italic">Noch keine Fotos. Empfehlung: mindestens 1 Foto vom gezählten Bestand.</div>
                        )}
                        {photos.length > 0 && (
                            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="inventory-photo-grid">
                                {photos.map((p) => (
                                    <div key={p.path} className="relative group aspect-square rounded-sm overflow-hidden border border-gray-200" data-testid={`inventory-photo-${p.path.split("/").pop()}`}>
                                        <PhotoThumb path={p.path} token={token} />
                                        <button
                                            type="button"
                                            onClick={() => removePhoto(p.path)}
                                            className="absolute top-1 right-1 bg-black/70 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                            title="Entfernen"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Submit */}
                {article && (
                    <div className="flex items-center gap-2 justify-end">
                        <Button type="button" variant="outline" onClick={reset} className="rounded-sm" data-testid="inventory-reset-btn">
                            Zurücksetzen
                        </Button>
                        <Button
                            type="button"
                            onClick={book}
                            disabled={booking || !locationId}
                            className="rounded-sm bg-[#111827] hover:bg-black text-white"
                            data-testid="inventory-book-btn"
                        >
                            {booking ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Bucht…</> : <><Check className="w-4 h-4 mr-2" />Inventur buchen</>}
                        </Button>
                    </div>
                )}
            </div>

            {/* SIDEBAR LIVE FEED */}
            <aside className="bg-gray-50 border border-gray-200 rounded-sm p-3 space-y-2 max-h-[600px] overflow-y-auto" data-testid="inventory-live-feed">
                <div className="flex items-center gap-2 sticky top-0 bg-gray-50 pb-2">
                    <Boxes className="w-4 h-4 text-[#FFCC00]" />
                    <h3 className="font-bold text-sm">Letzte Buchungen</h3>
                    <span className="ml-auto text-[10px] text-gray-500 font-mono-num">{feed.length}/10</span>
                </div>
                {feed.length === 0 && (
                    <div className="text-xs text-gray-400 italic py-4 text-center">Noch keine Buchungen in dieser Session.</div>
                )}
                {feed.map((f) => (
                    <div key={f.id} className="bg-white border border-gray-200 rounded-sm p-2 text-xs space-y-1" data-testid={`inventory-feed-${f.id}`}>
                        <div className="flex items-center justify-between gap-2">
                            <div className="font-bold truncate">{f.article}</div>
                            <span className={`shrink-0 font-mono-num font-bold ${f.delta > 0 ? "text-emerald-700" : f.delta < 0 ? "text-red-600" : "text-gray-500"}`}>
                                {f.delta > 0 ? "+" : ""}{f.delta}
                            </span>
                        </div>
                        <div className="text-gray-500 font-mono-num text-[10px]">{f.sku} · {f.location_code} · {f.qty} Stk</div>
                        {f.photo_paths?.length > 0 && (
                            <div className="flex gap-1 mt-1">
                                {f.photo_paths.slice(0, 4).map((p, idx) => (
                                    <div key={idx} className="w-9 h-9 rounded-sm overflow-hidden border border-gray-200">
                                        <PhotoThumb path={p} token={token} />
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="text-[10px] text-gray-400">{f.at.toLocaleTimeString("de-DE")}</div>
                    </div>
                ))}
            </aside>
            {showAiModal && article && (
                <AiEnrichArticleModal
                    article={article}
                    onClose={() => setShowAiModal(false)}
                    onApplied={(updated) => {
                        if (updated) {
                            setArticle(updated);
                            setPhotos((updated.photo_paths || []).map((p) => ({ path: p })).slice(0, MAX_PHOTOS));
                        }
                    }}
                />
            )}
            {scanMode && (
                <EanCameraScanner
                    onDetected={(code) => (scanMode === "location" ? resolveLocationScan(code) : findArticleByCode(code))}
                    onClose={() => setScanMode(null)}
                />
            )}
        </div>
    );
}
